import { MediaImage } from "@/components/admin/MediaAsset";
import { useEffect, useState } from "react";
import { Send, CalendarClock, Loader2, Plus, X, Users2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MediaGallery from "@/components/admin/MediaGallery";

export default function PublishDialog({ open, onClose, businessId, channel, text, onScheduled, initialImageUrl = "" }) {
  const [targets, setTargets] = useState([]);
  const [targetInput, setTargetInput] = useState("");
  const [sendAt, setSendAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [teamNumbers, setTeamNumbers] = useState([]);
  const [imageUrl, setImageUrl] = useState("");
  const [galleryOpen, setGalleryOpen] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/team-numbers").then((r) => setTeamNumbers(r.data || [])).catch(() => {});
      setImageUrl(initialImageUrl || "");
    }
  }, [open, initialImageUrl]);

  const addNumber = (num) => { if (num && !targets.includes(num)) setTargets((t) => [...t, num]); };

  const addTarget = () => {
    const digits = targetInput.replace(/[^0-9]/g, "");
    if (!digits) { toast.error("Nomor tidak boleh kosong"); return; }
    if (digits.length < 8 || digits.length > 15) { toast.error("Nomor WhatsApp tidak valid (8–15 digit)"); return; }
    const norm = digits.replace(/^0/, "62");
    if (targets.includes(norm)) { toast.error("Nomor sudah ditambahkan"); setTargetInput(""); return; }
    setTargets([...targets, norm]);
    setTargetInput("");
  };
  const removeTarget = (t) => setTargets(targets.filter((x) => x !== t));

  const sendNow = async () => {
    if (!businessId) { toast.error("Pilih bisnis dengan Fonnte terkonfigurasi"); return; }
    setBusy(true);
    try {
      const r = await api.post("/content/send-whatsapp", { business_id: businessId, text, channel, targets, image_url: imageUrl });
      r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message);
      if (r.data.ok) onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const schedule = async () => {
    if (!businessId) { toast.error("Pilih bisnis terlebih dahulu"); return; }
    if (!sendAt) { toast.error("Pilih tanggal & waktu"); return; }
    setBusy(true);
    try {
      await api.post("/scheduled-posts", {
        business_id: businessId,
        text,
        channel,
        targets,
        send_at: new Date(sendAt).toISOString(),
        image_url: imageUrl,
      });
      toast.success("Konten dijadwalkan");
      onScheduled && onScheduled();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Publikasikan ke WhatsApp</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Kirim konten {channel ? `(${channel})` : ""} ke tim sosmed via Fonnte, sekarang atau terjadwal.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl p-3 max-h-28 overflow-y-auto text-xs text-slate-700 whitespace-pre-wrap">{text}</div>

        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs text-slate-600 flex items-center gap-1"><ImageIcon size={12} /> Gambar (opsional)</Label>
            <button type="button" onClick={() => setGalleryOpen(true)} data-testid="publish-attach-image" className="text-xs text-[#2563EB] hover:underline">Lampirkan dari Galeri</button>
          </div>
          {imageUrl && (
            <div className="mt-2 flex items-center gap-2" data-testid="publish-image-preview">
              <MediaImage src={imageUrl} alt="attach" className="w-14 h-14 rounded-lg object-cover border border-[#E2E8F0]" />
              <button type="button" onClick={() => setImageUrl("")} data-testid="publish-image-remove" className="text-xs text-slate-600 hover:text-[#E11D48] flex items-center gap-1"><X size={13} /> Hapus gambar</button>
            </div>
          )}
        </div>

        <div>
          <Label className="text-xs text-slate-600">Nomor / Grup tujuan (bisa banyak, kosong = nomor admin bisnis)</Label>
          <div className="flex gap-2 mt-1">
            <Input
              value={targetInput}
              onChange={(e) => setTargetInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTarget(); } }}
              data-testid="publish-target-input"
              className="bg-[#F1F5F9] border-[#E2E8F0]"
              placeholder="628xxxxxxxxxx lalu Enter"
            />
            <Button type="button" onClick={addTarget} data-testid="publish-add-target-button" variant="outline" className="border-[#E2E8F0] bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl shrink-0"><Plus size={16} /></Button>
          </div>
          {teamNumbers.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="publish-team-numbers">
              <span className="text-[11px] text-slate-500 flex items-center gap-1"><Users2 size={11} /> Tim:</span>
              {teamNumbers.map((n) => (
                <button key={n.number} type="button" onClick={() => addNumber(n.number)} data-testid={`publish-team-pick-${n.number}`} className="text-xs bg-[#F1F5F9] border border-[#E2E8F0] rounded-full px-2.5 py-1 text-slate-700 hover:border-[#3B82F6] hover:text-slate-900 transition-colors">
                  + {n.label}
                </button>
              ))}
            </div>
          )}
          {targets.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2" data-testid="publish-target-chips">
              {targets.map((t) => (
                <span key={t} className="flex items-center gap-1 bg-[#E2E8F0] border border-[#E2E8F0] rounded-full pl-3 pr-1 py-1 text-xs">
                  {t}
                  <button onClick={() => removeTarget(t)} className="w-4 h-4 rounded-full hover:bg-[#F43F5E]/20 flex items-center justify-center"><X size={11} /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <Tabs defaultValue="now">
          <TabsList className="bg-[#F1F5F9] w-full">
            <TabsTrigger value="now" className="flex-1 data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">Kirim Sekarang</TabsTrigger>
            <TabsTrigger value="schedule" className="flex-1 data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">Jadwalkan</TabsTrigger>
          </TabsList>
          <TabsContent value="now" className="mt-4">
            <Button onClick={sendNow} disabled={busy} data-testid="publish-send-now-button" className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-xl">
              {busy ? <Loader2 size={16} className="animate-spin mr-1" /> : <Send size={16} className="mr-1" />} Kirim ke WhatsApp
            </Button>
          </TabsContent>
          <TabsContent value="schedule" className="mt-4 space-y-3">
            <div>
              <Label className="text-xs text-slate-600">Waktu kirim</Label>
              <Input type="datetime-local" value={sendAt} onChange={(e) => setSendAt(e.target.value)} data-testid="publish-schedule-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" />
            </div>
            <Button onClick={schedule} disabled={busy} data-testid="publish-schedule-button" className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
              {busy ? <Loader2 size={16} className="animate-spin mr-1" /> : <CalendarClock size={16} className="mr-1" />} Jadwalkan
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>

      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-2xl max-h-[min(85vh,calc(100dvh-2rem))] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Pilih Gambar</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Pilih gambar dari galeri media untuk dilampirkan ke pesan WhatsApp.</DialogDescription></DialogHeader>
          <MediaGallery picker onPick={(url) => { setImageUrl(url); setGalleryOpen(false); toast.success("Gambar dilampirkan"); }} />
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
